import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button'

import { PscPhrase } from '../../components/psc/psc-phrase';
import DependingObjects from '../../components/psc/lib/DependingObjects'
import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import DeleteButton from '../../components/psc/lib/DeleteButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import SingleSelect from '../../components/psc/lib/SingleSelect'
import Checkbox from '../../components/psc/lib/Checkbox';
import InfoModal from '../../components/psc/lib/InfoModal';

import useCadmObject from '../../hooks/useCadmObject'

const TypecodeGroupMapping = () => {
    let { objID } = useParams()
    const isNew = objID === "new" ? true : false
    objID = parseInt(objID,10)
    const objectUrl = '/v0/typecode_group_mappings' + (isNew ? '' : '/' + objID)

    const {
        cadmObjects,
    } = useCadmObject()

    // Fields
    const [name, setName] = useState('')
    const [cadmObjectID, setCadmObjectID] = useState(0)
    const [typecodeGroupID, setTypecodeGroupID] = useState(0)
    const [typecodeGroupMembers, setTypecodeGroupMembers] = useState([])

    // Read-only
    const [isUsed, setIsUsed] = useState(false)
    const [dependingObjects, setDependingObjects] = useState([])

    const [stdDocuments, setStdDocuments] = useState([])
    const [typecodeGroups, setTypecodeGroups] = useState([])
    const [stdDocumentIDsInUse, setStdDocumentIDsInUse] = useState([])

    const [memberStdDocuments, setMemberStdDocuments] = useState([])
    const [memberTypecodeGroupMembers, setMemberTypecodeGroupMembers] = useState([])

    const [archived, setArchived ] = useState([])

    const [showBulkAddModal, setShowBulkAddModal] = useState(false)
    const [bulkaddTypecodeGroupMemberID, setBulkaddTypecodeGroupMemberID] = useState(0)

    const [bulkaddStdSelectedDocuments, setBulkaddStdSelectedDocuments] = useState([])
    const [bulkaddStdDocumentFilter, setBulkaddStdDocumentFilter] = useState('')

    // Submit
    const [submitActive, setSubmitActive] = useState(false)
    const [error, setError] = useState(null)
    const [cloneActive, setCloneActive] = useState(false)

    const bulkSelectallLabel = useMemo(() =>
        bulkaddStdDocumentFilter
            ? PscPhrase('SELECT_ALL_IN_SEARCH')
            : PscPhrase('SELECT_ALL')
    , [bulkaddStdDocumentFilter]);

    const bulkaddStdDocuments = useMemo(() => {
        const term = bulkaddStdDocumentFilter.toUpperCase()

        // Create a list containing only the std documents that are not used
        const usedStdDocumentIDs = typecodeGroupMembers.map(m => m.std_document_id);
        const unused = stdDocuments
            .filter(item => item.cadm_object_id === cadmObjectID)
            .filter(std => usedStdDocumentIDs.indexOf(std.id) < 0);

        return unused
            .filter(item => item.label.toUpperCase().indexOf(term) >= 0);
    }, [typecodeGroupMembers, stdDocuments, cadmObjectID, bulkaddStdDocumentFilter])

    const bulkSelectallIsChecked = useMemo(() =>
        bulkaddStdDocuments.every(item => bulkaddStdSelectedDocuments.includes(item.id))
    , [bulkaddStdDocuments, bulkaddStdSelectedDocuments])

    useEffect(() => {
        if ( ! isNew) {
            fetchObject()
        }
    }, [isNew])

    useEffect(() => {
        fetch('/v0/std_documents', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setStdDocuments(json.results)
            })
        fetch('/v0/typecode_groups', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setTypecodeGroups(json.results)
            })
    }, [])

    useEffect(() => {
        const filtered = stdDocuments.filter(item => item.cadm_object_id === cadmObjectID)
        setMemberStdDocuments(filtered)
    }, [cadmObjectID, stdDocuments])

    useEffect(() => {
        if (!typecodeGroupID) {
            setMemberTypecodeGroupMembers([])
            return
        }
        const group = typecodeGroups.find(item => item.id === typecodeGroupID)
        if(!group) {
            setMemberTypecodeGroupMembers([])
            return
        }
        const members = group.typecode_group_members
        setMemberTypecodeGroupMembers(members)
    }, [typecodeGroupID, typecodeGroups])

    useEffect(() => {
        if (!typecodeGroupMembers) {
            return
        }
        const inUse = typecodeGroupMembers.map(m => m.std_document_id);
        setStdDocumentIDsInUse(inUse)

        setArchived(typecodeGroupMembers.filter(item => item.is_archived))
    }, [typecodeGroupMembers])

    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                fromBackend(json.result)
            })
            .catch(function(error) {
                console.log(error)
            })
    }

    const fromBackend = (obj) => {
        if (obj.members) {
            obj.members.map(function (m) {
                m.timestamp = m.id; // just to use same key 'timestamp' for all entries
            })
        } else {
            obj.members = [];
        }

        if (obj.typecode_group_id) {
            setTypecodeGroupID(obj.typecode_group_id ?? 0);
        }
        setName(obj.name);
        setCadmObjectID(obj.cadm_object_id ?? 0);
        setTypecodeGroupMembers(obj.members || []);
        setDependingObjects(obj.depending_objects);
        setIsUsed(obj.is_used);
    }

    const toBackend = () => {
        return {
            name: name,
            cadm_object_id: cadmObjectID,
            typecode_group_id: typecodeGroupID,
            members: typecodeGroupMembers
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []
        if ( ! name ) {
            errors.push('Name is required')
        }

        if ( ! cadmObjectID ) {
            errors.push('Configured Item is required')
        }

        if ( ! typecodeGroupID ) {
            errors.push('Type-Code List is required')
        }

        // Let's have some validation
        // check that list of standard document types is unique
        let stdDocumentIDs = typecodeGroupMembers.map(function (m) {
            return m.std_document_id;
        })
        var uniq = stdDocumentIDs
            .map((name) => {
                return {count: 1, name: name}
            })
            .reduce((a, b) => {
                a[b.name] = (a[b.name] || 0) + b.count
                return a
            }, {})
        var duplicates = Object.keys(uniq).filter((a) => uniq[a] > 1)
        if (duplicates.length > 0) {
            errors.push('A document Type has been specified more than once')
            let members = typecodeGroupMembers.map(function (m) {
                m.error = false
                return m
            })
            duplicates.map(function(dup) {
                members = members.map(function (m) {
                    if( m.std_document_id === dup ) {
                        m.error = true
                    }
                    return m
                })
            })
            setTypecodeGroupMembers(members)
        }

        // Check that we don't have members without and Std Doc set
        const emptyStd = typecodeGroupMembers.filter(m => !m.std_document_id);
        if (emptyStd.length > 0) {
            errors.push('Every entry needs to have a document Type specified')
            let members = typecodeGroupMembers.map(function (m) {
                m.error = false
                if(!m.std_document_id) {
                    m.error = true
                }
                return m
            })
            setTypecodeGroupMembers(members)
        }
        
        // Check that we don't have members without any Type-Code set
        const emptyTypeCode = typecodeGroupMembers.filter(m => !m.typecode_group_member_id);
        if (emptyTypeCode.length > 0) {
            errors.push('Every entry needs to have a Type-Code specified')
            let members = typecodeGroupMembers.map(function (m) {
                m.typecode_group_error = false
                if(!m.typecode_group_member_id) {
                    m.typecode_group_error = true
                }
                return m
            })
            setTypecodeGroupMembers(members)
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }

        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        // Update the stored project, in case the project uses this mapping
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'typecode_group_mappings',
            objID: objID,
            data: toBackend(),
        }
    }

    const clone = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)
        setCloneActive(true)

        let url = objectUrl+'/clone'
        return {
            objType: 'typecode_group_mappings',
            objID: objID,
            url: url,
            data: toBackend(),
        }
    }

    const removeMember = (member) => {
        let members = typecodeGroupMembers.filter(function (m) {
            if(m.timestamp === member.timestamp) { return false }
            return true;
        });
        setTypecodeGroupMembers(members)
    }

    const archiveMember = (member) => {
        const members = typecodeGroupMembers.map(function (m) {
            if(m.timestamp === member.timestamp) {
                m.is_archived = true
            }
            return m
        });
        setTypecodeGroupMembers(members)
        setArchived(typecodeGroupMembers.filter(item => item.is_archived))
    }

    const unarchiveMember = (member) => {
        const members = typecodeGroupMembers.map(function (m) {
            if(m.timestamp === member.timestamp) {
                m.is_archived = false
            }
            return m
        });
        setTypecodeGroupMembers(members)
        setArchived(typecodeGroupMembers.filter(item => item.is_archived))
    }

    const addMember = () => {
        if ( ! typecodeGroupID ) {
            setError(['Please specify a Type-Code List first'])
            return
        }
        setError([])

        // timestamp to have a unique key in the table
        const newMembers = [{ id: 0, std_document_id: 0, typecode_group_member_id: 0, timestamp: new Date().getTime() }];

        setTypecodeGroupMembers([...typecodeGroupMembers, ...newMembers]);
    }

    const updateMember = (field, member, selected) => {
        const value = selected.id
        
        let members = typecodeGroupMembers.filter(function (m) {
            if(m.timestamp === member.timestamp) {
                m[field] = value
            }
            return true;
        });
        setTypecodeGroupMembers(members)
    }

    const bulkAddMembers = () => {
        handleBulkAddModalClose();
        const newMembers = bulkaddStdSelectedDocuments
            .map(id => {
                return {
                    id: 0,
                    typecode_group_member_id: bulkaddTypecodeGroupMemberID,
                    std_document_id: id,
                    timestamp: new Date().getTime() + '_' + id
                }
            });

        const updatedMembers = [...typecodeGroupMembers, ...newMembers];
        setTypecodeGroupMembers(updatedMembers);

        //setBulkaddStdDocuments(unused.filter(obj => obj.cadm_object_id === cadmObjectID))        
        return
    }

    const handleChangeBulkAddStdDocument = ({ target: { id, checked } }) => {
        const selectedDocuments = checked
            ? [...bulkaddStdSelectedDocuments, Number(id)]
            : bulkaddStdSelectedDocuments.filter(item => item !== Number(id));
        setBulkaddStdSelectedDocuments(selectedDocuments);
    }

    const handleBulkSelectall = ({ target: { checked }}) => {
        const selectedDocuments = checked
            ? bulkaddStdDocuments.map(item => Number(item.id))
            : [];
        setBulkaddStdSelectedDocuments(selectedDocuments);
    }

    const handleBulkAddModalClose = () => {
        setShowBulkAddModal(false);
        setBulkaddStdSelectedDocuments([]);
        setBulkaddStdDocumentFilter('');
    }
    
    const renderArchived = () => {
        let archivedBlock = null

        if( archived.length === 0 ) {
            archivedBlock = (
                <div>
                    <label className="control-label">Archived Type-Code Mappings</label>
                    <span className="help-block m-b-none m-t-none">
                        <small className="form-info-text" > {PscPhrase('TYPE_CODE_MAP_ARCHIVED_NOTE')}</small>
                    </span>
                        
                    <ul className="list-group clear-list m-t">
                        <li className="list-group-item">
                                Nothing Archived
                        </li>
                    </ul>
                </div>
            )
        } else {
            archivedBlock = (
                <div>
                    <label className="control-label">Archived Type-Code Mappings</label>
                    <span className="help-block m-b-none m-t-none">
                        <small className="form-info-text" > {PscPhrase('TYPE_CODE_MAP_ARCHIVED_NOTE')}</small>
                    </span>
                    <ul className="list-group clear-list m-t">
                        {archived.map(function(obj) {
                            const std_document = memberStdDocuments.find(item => item.id === obj.std_document_id)
                            const typecode_group_member = memberTypecodeGroupMembers.find(item => item.id === obj.typecode_group_member_id)

                            let label = std_document ? std_document.name : '<Undefined>'
                            label += ' | '
                            label += typecode_group_member ? typecode_group_member.code + ' - ' + typecode_group_member.name : '<Undefined>'
                                
                            return (
                                <li className="list-group-item" key={obj.id || obj.timestamp}>
                                    {obj.is_used === true &&
                                            <DependingObjects list={obj.depending_objects} size="xs" />
                                    }
                                    {obj.is_used !== true &&
                                            <button type="button" className="btn btn-xs btn-warning" onClick={e => removeMember(obj)}>Remove</button>
                                    }
                                        &nbsp;
                                    <button type="button" className="btn btn-xs btn-light" onClick={e => unarchiveMember(obj)}>Restore</button>
                                        &nbsp;
                                        &nbsp;
                                    {label}
                                </li>
                            )
                        })}
                    </ul>
                </div>
                
            )
        }
        return archivedBlock
    }

    const renderBulkAddModalBodyContent = () => (
        <div>
            <div className="form-group">
                <label>Select a Type-Code</label>
                {typecodeGroups &&
                    <SingleSelect
                        value={bulkaddTypecodeGroupMemberID}
                        onChange={value => setBulkaddTypecodeGroupMemberID(value.id)}
                        options={memberTypecodeGroupMembers}
                    />
                }
            </div>

            <div className="form-group">
                <label>Select all document Types that will use this Type-Code</label>
                <input
                    type="text"
                    className="input-sm form-control input-s-sm inline"
                    value={bulkaddStdDocumentFilter}
                    onChange={e => setBulkaddStdDocumentFilter(e.target.value)}
                    placeholder="Search.."
                />
            </div>
            <div className="overflow-auto" style={{ height: 400 }}>
                {bulkaddStdDocuments.length > 0 ?
                <div className="form-check form-check-sticky">
                    <Checkbox
                        id="bulk-select-all"
                        name="bulk-select-all"
                        onChange={handleBulkSelectall}
                        isChecked={bulkSelectallIsChecked}
                    />
                    <label htmlFor="bulk-select-all">{bulkSelectallLabel}</label>
                </div>
                : <label htmlFor="bulk-select-all">{PscPhrase('NO_RESULTS')}</label>}
                {bulkaddStdDocuments.map(({ id, name }) => (
                    <div className="form-check" key={id}>
                        <Checkbox
                            id={id}
                            name={id}
                            onChange={handleChangeBulkAddStdDocument}
                            isChecked={bulkaddStdSelectedDocuments.includes(id)}
                        />
                        <label htmlFor={id}>{name}</label>
                    </div>
                ))}
            </div>
        </div>
    )

    const renderBulkAddModalFooterContent = () => (
        <>
            <Button variant="light" onClick={handleBulkAddModalClose}>
                Close
            </Button>
            <Button variant="primary" onClick={bulkAddMembers}>
                OK
            </Button>
        </>
    )

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>)
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Type-Code Map: {name || '<New Type-Code Map>'}</h2>

                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Name
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="name" value={name} onChange={e => setName(e.target.value)} required />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Configured Item
                                        {!isUsed && requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={cadmObjectID}
                                            onChange={value => setCadmObjectID(value.id)} 
                                            disabled={isUsed}
                                            options={cadmObjects.filter(item => item.do_use_typecode_group_mapping )}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Type-Code List
                                        {!isUsed && requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={typecodeGroupID}
                                            onChange={value => setTypecodeGroupID(value.id)} 
                                            options={typecodeGroups.filter(item => item.cadm_object_id === cadmObjectID )}
                                            disabled={isUsed}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Type-Code Map</label>
                                    <div className="col-sm-8">

                                        <div>
                                            <table className="table table-striped table-bordered table-hover dataTable" >
                                                <thead>
                                                    <tr>
                                                        <th>Document Type</th>
                                                        <th>Type-Code</th>
                                                        <th style={{width: '150px'}}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {typecodeGroupMembers.map(function(obj) {
                                                        if( obj.is_archived ) {
                                                            return null
                                                        }
                                                            
                                                        // const inUse = typecodeGroupMembers.map(m => m.std_document_id);
                                                        // .filter(item => !inUse.includes(item.id))
                                                        return (
                                                            <tr key={obj.id || obj.timestamp} className={(obj.error || obj.typecode_group_error) ? 'error' : ''}>
                                                                <td>
                                                                    <SingleSelect
                                                                        name="std_document_id"
                                                                        value={obj.std_document_id}
                                                                        onChange={value => updateMember('std_document_id', obj, value)}
                                                                        options={memberStdDocuments.filter(item => item.id === obj.std_document_id || !stdDocumentIDsInUse.includes(item.id))}
                                                                        disabled={obj.is_used}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <SingleSelect
                                                                        name="typecode_group_member_id"
                                                                        value={obj.typecode_group_member_id}
                                                                        onChange={value => updateMember('typecode_group_member_id', obj, value)}
                                                                        options={memberTypecodeGroupMembers}
                                                                        disabled={obj.is_used}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    {obj.is_used === true &&
                                                                        <DependingObjects list={obj.depending_objects} size="xs" />
                                                                    }
                                                                    {obj.is_used !== true &&
                                                                        <button type="button" className="btn btn-xs btn-warning" onClick={e => removeMember(obj)}>Remove</button>
                                                                    }
                                                                        &nbsp;
                                                                    {!!obj.std_document_id && !!obj.typecode_group_member_id &&
                                                                        <button type="button" className="btn btn-xs btn-light" onClick={e => archiveMember(obj)}>Archive</button>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        )
                                                    })}
                                                    <tr>
                                                        <td>&nbsp;</td>
                                                        <td>&nbsp;</td>
                                                        <td>
                                                            { typecodeGroupID > 0 &&
                                                                <div>
                                                                    <button type="button" className="btn btn-xs btn-primary" onClick={addMember}>Add</button>
                                                                    &nbsp;
                                                                    <button type="button" className="btn btn-xs btn-light" onClick={() => { setShowBulkAddModal(true) }}>Bulk add</button>
                                                                </div>
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th>Document Type</th>
                                                        <th>Type-Code</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                    </label>
                                    <div className="col-sm-8">
                                        {renderArchived()}
                                    </div>
                                </div>
                                
                                <div className="hr-line-dashed"></div>
                                
                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        <SaveButton
                                            buttonVariant='warning'
                                            visible={!isNew}
                                            spinnerActive={cloneActive}
                                            label='Clone'
                                            onClick={clone}
                                            onError={handleSubmitError}
                                        />
            
                                        <DependingObjects list={dependingObjects} />

                                        <DeleteButton
                                           isHidden={isNew || isUsed}
                                           url={objectUrl}
                                           onError={handleSubmitError}
                                        />

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <InfoModal
                titleIcon="fa fa-files-o"
                title="Add several document types to a type-code"
                show={showBulkAddModal}
                bodyContent={renderBulkAddModalBodyContent()}
                footerContent={renderBulkAddModalFooterContent()}
                onClose={handleBulkAddModalClose}
            />
        </div>
    )
};

export default TypecodeGroupMapping
